import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Leaf, Shield, BarChart3, Globe, Users, FileText, Bot, CheckCircle,
  ArrowRight, Play, Star, Building2, TrendingUp, Lock, Zap, Award,
  ChevronRight, Target, Scale, Layers, Clock, FileCheck, Sparkles,
  ChevronDown, Briefcase, Eye
} from 'lucide-react';

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, color }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg hover:border-emerald-200 transition-all duration-300 group">
    <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
      <Icon className="text-white" size={24} />
    </div>
    <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
    <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
  </div>
);

// Stat Card Component
const StatCard = ({ value, label, suffix = '' }) => (
  <div className="text-center">
    <div className="text-4xl md:text-5xl font-bold text-white mb-2">
      {value}<span className="text-emerald-300">{suffix}</span>
    </div>
    <div className="text-emerald-200 text-sm">{label}</div>
  </div>
);

// Testimonial Card Component
const TestimonialCard = ({ quote, author, role, company }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
    <div className="flex gap-1 mb-4">
      {[...Array(5)].map((_, i) => (
        <Star key={i} size={16} className="text-amber-400 fill-amber-400" />
      ))}
    </div>
    <p className="text-slate-700 mb-4 italic">&ldquo;{quote}&rdquo;</p>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold">
        {author.charAt(0)}
      </div>
      <div>
        <p className="font-semibold text-slate-900">{author}</p>
        <p className="text-sm text-slate-500">{role}, {company}</p>
      </div>
    </div>
  </div>
);

// Regulation Badge Component
const RegulationBadge = ({ name, description }) => (
  <div className="flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-3 hover:bg-emerald-50 transition-colors">
    <CheckCircle size={20} className="text-emerald-600 flex-shrink-0" />
    <div>
      <span className="font-semibold text-slate-900">{name}</span>
      <span className="text-slate-500 text-sm ml-2">{description}</span>
    </div>
  </div>
);

// Main Landing Page Component
const LandingPage = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [showPortalMenu, setShowPortalMenu] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Leaf className="text-white" size={24} />
              </div>
              <span className="text-xl font-bold text-slate-900">Disclosure OS</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-emerald-600 transition-colors">Features</a>
              <a href="#regulations" className="text-slate-600 hover:text-emerald-600 transition-colors">Regulations</a>
              <a href="#testimonials" className="text-slate-600 hover:text-emerald-600 transition-colors">Testimonials</a>
              <a href="#pricing" className="text-slate-600 hover:text-emerald-600 transition-colors">Pricing</a>
              
              {/* Portal Access Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowPortalMenu(!showPortalMenu)}
                  onBlur={() => setTimeout(() => setShowPortalMenu(false), 200)}
                  data-testid="portal-access-dropdown"
                  className="flex items-center gap-1 text-slate-600 hover:text-emerald-600 transition-colors"
                >
                  Portal Access
                  <ChevronDown size={16} className={`transition-transform ${showPortalMenu ? 'rotate-180' : ''}`} />
                </button>
                
                {showPortalMenu && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-100 py-2 z-50">
                    <Link
                      to="/regulator-portal"
                      data-testid="regulator-portal-link"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Eye size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Regulator Portal</p>
                        <p className="text-xs text-slate-500">ESMA, SEC, FCA access</p>
                      </div>
                    </Link>
                    <Link
                      to="/auditor-assurance-portal"
                      data-testid="auditor-portal-link"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Briefcase size={20} className="text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Auditor Portal</p>
                        <p className="text-xs text-slate-500">ESG assurance access</p>
                      </div>
                    </Link>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                data-testid="landing-sign-in-btn"
                className="px-4 py-2 text-slate-700 hover:text-emerald-600 font-medium transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                data-testid="landing-get-started-btn"
                className="px-5 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2"
              >
                Get Started <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full text-emerald-700 text-sm font-medium mb-6">
                <Sparkles size={16} />
                AI-Powered ESG Compliance Platform
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Transform Your{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">
                  ESG Reporting
                </span>{' '}
                Journey
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Simplify compliance with CSRD, EU Taxonomy, and global ESG frameworks. 
                Our AI-powered platform automates data collection, analysis, and reporting 
                so you can focus on making a real impact.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/signup"
                  data-testid="landing-start-trial-btn"
                  className="px-8 py-4 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all hover:shadow-lg hover:shadow-emerald-200 flex items-center justify-center gap-2"
                >
                  Start Free Trial <ArrowRight size={20} />
                </Link>
                <button
                  onClick={() => setShowVideo(true)}
                  data-testid="landing-watch-demo-btn"
                  className="px-8 py-4 bg-white border-2 border-slate-200 text-slate-700 rounded-xl font-semibold hover:border-emerald-300 hover:text-emerald-600 transition-all flex items-center justify-center gap-2"
                >
                  <Play size={20} className="fill-current" /> Watch Demo
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6">
                <div className="flex -space-x-2">
                  {['bg-blue-500', 'bg-purple-500', 'bg-amber-500', 'bg-rose-500'].map((color, i) => (
                    <div key={i} className={`w-10 h-10 rounded-full ${color} border-2 border-white flex items-center justify-center text-white text-xs font-bold`}>
                      {String.fromCharCode(65 + i)}
                    </div>
                  ))}
                </div>
                <div className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">500+</span> companies trust Disclosure OS
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-br from-emerald-200 to-teal-200 rounded-full blur-3xl opacity-30"></div>
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    <span className="ml-4 text-white/80 text-sm">ESG Dashboard</span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-emerald-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-600">87%</p>
                      <p className="text-xs text-slate-600">Compliance</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">-23%</p>
                      <p className="text-xs text-slate-600">Emissions</p>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-purple-600">A+</p>
                      <p className="text-xs text-slate-600">ESG Rating</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle size={18} className="text-emerald-600" />
                        <span className="text-sm text-slate-700">CSRD Compliance</span>
                      </div>
                      <span className="text-xs text-emerald-600 font-medium">Complete</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle size={18} className="text-emerald-600" />
                        <span className="text-sm text-slate-700">EU Taxonomy Aligned</span>
                      </div>
                      <span className="text-xs text-emerald-600 font-medium">68%</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Clock size={18} className="text-amber-600" />
                        <span className="text-sm text-slate-700">Scope 3 Analysis</span>
                      </div>
                      <span className="text-xs text-amber-600 font-medium">In Progress</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Our Mission</h2>
            <p className="text-xl text-emerald-100 max-w-3xl mx-auto leading-relaxed">
              To democratize ESG reporting by providing every organization—regardless of size—with 
              the tools and intelligence needed to measure, manage, and communicate their environmental 
              and social impact. We believe transparency drives progress, and progress drives a sustainable future.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatCard value="50" suffix="%" label="Average time saved on reporting" />
            <StatCard value="100" suffix="+" label="Regulatory frameworks covered" />
            <StatCard value="99.9" suffix="%" label="Data accuracy rate" />
            <StatCard value="24/7" suffix="" label="AI-powered support" />
          </div>
        </div>
      </section>

      {/* Why Disclosure OS Section */}
      <section className="py-20 bg-slate-50" id="features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Disclosure OS?
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Built by sustainability experts and powered by cutting-edge AI, 
              our platform transforms complex ESG requirements into actionable insights.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon={Bot}
              title="7 Specialized AI Agents"
              description="Our autonomous AI team handles data analysis, risk assessment, report generation, and strategic recommendations—working 24/7 so you don't have to."
              color="bg-gradient-to-br from-violet-500 to-purple-600"
            />
            <FeatureCard
              icon={Shield}
              title="Complete Regulatory Coverage"
              description="Stay compliant with CSRD, ESRS, EU Taxonomy, TCFD, GRI, SASB, SEC Climate Rules, and 100+ other frameworks—all in one platform."
              color="bg-gradient-to-br from-emerald-500 to-teal-600"
            />
            <FeatureCard
              icon={BarChart3}
              title="Double Materiality Assessment"
              description="Identify what matters most with our IRO matrix. Assess both financial impact and environmental/social impact to prioritize your ESG efforts."
              color="bg-gradient-to-br from-blue-500 to-cyan-600"
            />
            <FeatureCard
              icon={Layers}
              title="Scope 1, 2 & 3 Tracking"
              description="Comprehensive GHG emissions tracking across your entire value chain. Auto-detect hotspots and get AI-powered reduction recommendations."
              color="bg-gradient-to-br from-amber-500 to-orange-600"
            />
            <FeatureCard
              icon={FileCheck}
              title="Audit-Ready Reports"
              description="Generate professional, assurance-ready reports in PDF and Excel. Full audit trail and evidence management built-in."
              color="bg-gradient-to-br from-rose-500 to-pink-600"
            />
            <FeatureCard
              icon={TrendingUp}
              title="Peer Benchmarking"
              description="See how you stack up against industry peers. Benchmark your ESG performance and identify competitive advantages."
              color="bg-gradient-to-br from-indigo-500 to-blue-600"
            />
          </div>
        </div>
      </section>

      {/* Regulations Covered */}
      <section className="py-20 bg-white" id="regulations">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Full Regulatory Coverage
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Navigate the complex landscape of ESG regulations with confidence. 
                Our platform covers all major frameworks and automatically maps your data 
                to meet multiple reporting requirements simultaneously.
              </p>
              <div className="space-y-3">
                <RegulationBadge name="CSRD / ESRS" description="EU Corporate Sustainability Reporting Directive" />
                <RegulationBadge name="EU Taxonomy" description="Sustainable Finance Classification System" />
                <RegulationBadge name="TCFD" description="Task Force on Climate-related Financial Disclosures" />
                <RegulationBadge name="GRI Standards" description="Global Reporting Initiative" />
                <RegulationBadge name="SASB" description="Sustainability Accounting Standards Board" />
                <RegulationBadge name="SEC Climate Rules" description="US Securities Climate Disclosure" />
                <RegulationBadge name="GHG Protocol" description="Greenhouse Gas Accounting Standard" />
                <RegulationBadge name="CDP" description="Carbon Disclosure Project" />
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 text-white">
                <div className="flex items-center gap-3 mb-6">
                  <Globe size={28} className="text-emerald-400" />
                  <h3 className="text-xl font-bold">Global Compliance Hub</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">EU Regulations</span>
                    <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div className="w-[95%] h-full bg-emerald-500 rounded-full"></div>
                    </div>
                    <span className="text-emerald-400 font-medium">95%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">US Requirements</span>
                    <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div className="w-[88%] h-full bg-blue-500 rounded-full"></div>
                    </div>
                    <span className="text-blue-400 font-medium">88%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Global Standards</span>
                    <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div className="w-[92%] h-full bg-purple-500 rounded-full"></div>
                    </div>
                    <span className="text-purple-400 font-medium">92%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Industry Specific</span>
                    <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div className="w-[85%] h-full bg-amber-500 rounded-full"></div>
                    </div>
                    <span className="text-amber-400 font-medium">85%</span>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <p className="text-slate-400 text-sm">
                    One data entry. Multiple reports. Complete compliance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-50" id="testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Trusted by Sustainability Leaders
            </h2>
            <p className="text-xl text-slate-600">
              See why companies choose Disclosure OS for their ESG reporting needs
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <TestimonialCard
              quote="Disclosure OS reduced our CSRD preparation time by 60%. The AI agents handle the heavy lifting while we focus on strategy."
              author="Sarah Chen"
              role="Chief Sustainability Officer"
              company="TechCorp Global"
            />
            <TestimonialCard
              quote="The double materiality assessment feature is a game-changer. We identified risks we hadn't even considered before."
              author="Marcus Weber"
              role="Head of ESG"
              company="EuroManufacturing AG"
            />
            <TestimonialCard
              quote="Finally, a platform that speaks both sustainability and finance. Our investors love the clear, data-driven reports."
              author="Elena Rodriguez"
              role="CFO"
              company="GreenEnergy Solutions"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <Zap size={16} />
            Start in under 5 minutes
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Your ESG Reporting?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Join 500+ companies already using Disclosure OS to simplify compliance, 
            reduce risk, and drive sustainable growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="px-8 py-4 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-500 transition-all hover:shadow-lg hover:shadow-emerald-500/20 flex items-center justify-center gap-2"
            >
              Start Free 14-Day Trial <ArrowRight size={20} />
            </Link>
            <Link
              to="/login"
              className="px-8 py-4 bg-white/10 text-white border border-white/20 rounded-xl font-semibold hover:bg-white/20 transition-all flex items-center justify-center gap-2"
            >
              Schedule a Demo
            </Link>
          </div>
          <p className="mt-6 text-slate-400 text-sm">
            No credit card required • Full access to all features • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Leaf className="text-white" size={24} />
                </div>
                <span className="text-xl font-bold text-white">Disclosure OS</span>
              </div>
              <p className="text-slate-400 text-sm">
                AI-powered ESG compliance platform helping companies measure, manage, 
                and report their sustainability performance.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#features" className="hover:text-emerald-400 transition-colors">Features</a></li>
                <li><a href="#regulations" className="hover:text-emerald-400 transition-colors">Regulations</a></li>
                <li><a href="#pricing" className="hover:text-emerald-400 transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-emerald-400 transition-colors">Integrations</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-emerald-400 transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-emerald-400 transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-emerald-400 transition-colors">Case Studies</a></li>
                <li><a href="#" className="hover:text-emerald-400 transition-colors">Webinars</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-emerald-400 transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-emerald-400 transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-emerald-400 transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-emerald-400 transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © 2025 Disclosure OS. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-slate-500 text-sm">
              <a href="#" className="hover:text-emerald-400 transition-colors">Terms</a>
              <a href="#" className="hover:text-emerald-400 transition-colors">Privacy</a>
              <a href="#" className="hover:text-emerald-400 transition-colors">Cookies</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      {showVideo && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowVideo(false)}
        >
          <div className="bg-slate-900 rounded-2xl p-8 max-w-2xl w-full text-center">
            <div className="w-20 h-20 mx-auto bg-emerald-600 rounded-full flex items-center justify-center mb-6">
              <Play size={40} className="text-white ml-2" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Demo Video Coming Soon</h3>
            <p className="text-slate-400 mb-6">
              Our product demo video is currently in production. 
              Sign up for a personalized demo instead!
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700"
            >
              Get Started <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
